import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const KnowledgeBasePanel = ({ analysisHistory }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [selectedArticle, setSelectedArticle] = useState(null);

  // Mock knowledge base articles (in production, this would come from database)
  const knowledgeBase = [
    {
      id: 1,
      title: 'Database Connection Pool Exhaustion',
      category: 'database',
      errorType: 'connection_error',
      summary: 'Connection pool exhaustion due to unclosed connections and high concurrent load',
      rootCause: 'Application not properly closing database connections after queries, leading to pool exhaustion under load',
      solution: 'Implement connection pooling with proper lifecycle management, add connection timeout, and use connection pool monitoring',
      preventiveMeasures: ['Use ORM with automatic connection management', 'Implement connection pool monitoring', 'Add connection leak detection'],
      relatedErrors: ['ECONNREFUSED', 'ETIMEDOUT', 'Pool timeout'],
      resolvedCount: 12,
      avgResolutionTime: '45 minutes',
      successRate: 95,
      lastUpdated: '2026-02-10T14:30:00Z',
      tags: ['database', 'connection', 'performance']
    },
    {
      id: 2,
      title: 'API Rate Limit Exceeded',
      category: 'api',
      errorType: 'rate_limit_error',
      summary: 'Third-party API rate limits exceeded during peak traffic periods',
      rootCause: 'No rate limiting implementation on client side, causing burst requests that exceed provider limits',
      solution: 'Implement client-side rate limiting with token bucket algorithm, add request queuing, and implement exponential backoff',
      preventiveMeasures: ['Add rate limit monitoring', 'Implement request caching', 'Use batch API calls where possible'],
      relatedErrors: ['429 Too Many Requests', 'Rate limit exceeded'],
      resolvedCount: 8,
      avgResolutionTime: '30 minutes',
      successRate: 100,
      lastUpdated: '2026-02-09T10:15:00Z',
      tags: ['api', 'rate-limit', 'external-service']
    },
    {
      id: 3,
      title: 'Memory Leak in Event Listeners',
      category: 'memory',
      errorType: 'memory_leak',
      summary: 'Progressive memory increase due to unremoved event listeners in React components',
      rootCause: 'Event listeners added in useEffect without proper cleanup, causing memory accumulation on component remounts',
      solution: 'Add cleanup functions to useEffect hooks, use AbortController for fetch requests, and implement proper event listener removal',
      preventiveMeasures: ['Use ESLint rules for useEffect cleanup', 'Implement memory profiling in CI/CD', 'Add memory usage monitoring'],
      relatedErrors: ['Out of memory', 'Heap size exceeded'],
      resolvedCount: 5,
      avgResolutionTime: '2 hours',
      successRate: 88,
      lastUpdated: '2026-02-08T16:45:00Z',
      tags: ['memory', 'react', 'performance']
    },
    {
      id: 4,
      title: 'Authentication Token Expiration',
      category: 'authentication',
      errorType: 'auth_error',
      summary: 'Users experiencing session timeouts without proper token refresh mechanism',
      rootCause: 'No automatic token refresh implementation, causing hard session expiration',
      solution: 'Implement silent token refresh with refresh tokens, add token expiration monitoring, and handle 401 responses gracefully',
      preventiveMeasures: ['Use refresh token rotation', 'Implement token expiration warnings', 'Add session persistence'],
      relatedErrors: ['401 Unauthorized', 'Token expired', 'Invalid token'],
      resolvedCount: 15,
      avgResolutionTime: '1 hour',
      successRate: 92,
      lastUpdated: '2026-02-11T09:20:00Z',
      tags: ['authentication', 'security', 'session']
    }
  ];

  const filteredArticles = knowledgeBase?.filter(article => {
    const matchesSearch = searchTerm === '' ||
      article?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      article?.summary?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      article?.tags?.some(tag => tag?.toLowerCase()?.includes(searchTerm?.toLowerCase()));

    const matchesCategory = filterCategory === 'all' || article?.category === filterCategory;

    return matchesSearch && matchesCategory;
  });

  const categories = [
    { value: 'all', label: 'All Categories', icon: 'Grid' },
    { value: 'database', label: 'Database', icon: 'Database' },
    { value: 'api', label: 'API', icon: 'Globe' },
    { value: 'memory', label: 'Memory', icon: 'Cpu' },
    { value: 'authentication', label: 'Authentication', icon: 'Lock' }
  ];

  const handleExport = (article) => {
    const exportData = {
      title: article?.title,
      category: article?.category,
      rootCause: article?.rootCause,
      solution: article?.solution,
      preventiveMeasures: article?.preventiveMeasures,
      exportedAt: new Date()?.toISOString()
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `knowledge-base-${article?.id}.json`;
    a?.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Knowledge Base</h3>
        <p className="text-sm text-gray-600 mt-1">Searchable repository of previous analyses and proven solutions</p>
      </div>

      {/* Search and Filters */}
      <div className="space-y-3">
        <div className="relative">
          <Icon name="Search" className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            placeholder="Search by title, description, or tags..."
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>

        <div className="flex gap-2 overflow-x-auto pb-2">
          {categories?.map(category => (
            <button
              key={category?.value}
              onClick={() => setFilterCategory(category?.value)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-all ${
                filterCategory === category?.value
                  ? 'bg-purple-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Icon name={category?.icon} className="w-4 h-4" />
              {category?.label}
            </button>
          ))}
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="BookOpen" className="w-5 h-5 text-blue-600" />
            <span className="text-sm font-medium text-gray-700">Total Articles</span>
          </div>
          <p className="text-2xl font-bold text-blue-600">{knowledgeBase?.length}</p>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="CheckCircle" className="w-5 h-5 text-green-600" />
            <span className="text-sm font-medium text-gray-700">Resolved Issues</span>
          </div>
          <p className="text-2xl font-bold text-green-600">
            {knowledgeBase?.reduce((sum, a) => sum + a?.resolvedCount, 0)}
          </p>
        </div>
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-200">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Target" className="w-5 h-5 text-purple-600" />
            <span className="text-sm font-medium text-gray-700">Avg Success Rate</span>
          </div>
          <p className="text-2xl font-bold text-purple-600">
            {(knowledgeBase?.reduce((sum, a) => sum + a?.successRate, 0) / knowledgeBase?.length)?.toFixed(1)}%
          </p>
        </div>
      </div>

      {/* Articles List */}
      {filteredArticles?.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
          <Icon name="Search" className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">No articles found</p>
          <p className="text-sm text-gray-500 mt-1">Try adjusting your search or filters</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredArticles?.map((article) => (
            <div
              key={article?.id}
              className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
            >
              <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-semibold text-gray-900">{article?.title}</h4>
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                        {article?.category}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">{article?.summary}</p>
                    <div className="flex flex-wrap gap-2">
                      {article?.tags?.map((tag, idx) => (
                        <span key={idx} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedArticle(selectedArticle === article?.id ? null : article?.id)}
                    className="ml-3 text-gray-400 hover:text-gray-600"
                  >
                    <Icon name={selectedArticle === article?.id ? 'ChevronUp' : 'ChevronDown'} className="w-5 h-5" />
                  </button>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center p-2 bg-gray-50 rounded">
                    <p className="text-xs text-gray-500 mb-1">Resolved</p>
                    <p className="text-sm font-bold text-gray-900">{article?.resolvedCount} times</p>
                  </div>
                  <div className="text-center p-2 bg-gray-50 rounded">
                    <p className="text-xs text-gray-500 mb-1">Avg Time</p>
                    <p className="text-sm font-bold text-gray-900">{article?.avgResolutionTime}</p>
                  </div>
                  <div className="text-center p-2 bg-gray-50 rounded">
                    <p className="text-xs text-gray-500 mb-1">Success Rate</p>
                    <p className="text-sm font-bold text-green-600">{article?.successRate}%</p>
                  </div>
                </div>
              </div>

              {selectedArticle === article?.id && (
                <div className="border-t border-gray-200 bg-gray-50 p-5 space-y-4">
                  <div>
                    <h5 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <Icon name="AlertCircle" className="w-4 h-4 text-red-600" />
                      Root Cause
                    </h5>
                    <p className="text-sm text-gray-700 bg-white p-3 rounded border border-gray-200">{article?.rootCause}</p>
                  </div>

                  <div>
                    <h5 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <Icon name="CheckCircle" className="w-4 h-4 text-green-600" />
                      Solution
                    </h5>
                    <p className="text-sm text-gray-700 bg-white p-3 rounded border border-gray-200">{article?.solution}</p>
                  </div>

                  <div>
                    <h5 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <Icon name="Shield" className="w-4 h-4 text-blue-600" />
                      Preventive Measures
                    </h5>
                    <ul className="space-y-2">
                      {article?.preventiveMeasures?.map((measure, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                          <Icon name="Check" className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                          <span>{measure}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="flex gap-3 pt-3">
                    <Button
                      onClick={() => handleExport(article)}
                      variant="outline"
                      className="flex-1"
                    >
                      <Icon name="Download" className="w-4 h-4 mr-2" />
                      Export Article
                    </Button>
                    <Button className="flex-1 bg-purple-600 hover:bg-purple-700">
                      <Icon name="Copy" className="w-4 h-4 mr-2" />
                      Apply Solution
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default KnowledgeBasePanel;